import { Box, BoxProps, Button, Flex, Stack, Text, useColorModeValue } from '@chakra-ui/react'
import Link from 'next/link'
import { DomainObjects, LocatableEntityRecord } from 'projectlink-common'
import { FC, useEffect, useState } from 'react'
import { BiLockOpenAlt } from 'react-icons/bi'
import Avatar from 'src/common/Avatar'
import ConditionallyWrap from 'src/common/ConditionallyWrap'
import useUserContext from 'src/hooks/useUserContext'
import { routes } from 'src/navigation/util'
import { Caption, H5 } from 'src/typography/typography'
import { capitalize } from 'src/util'
import { domainColor } from 'src/util/style'

interface SnippetProps extends BoxProps {
  hit: LocatableEntityRecord
  register?: (_e) => void,
  isPartner?: boolean,
  /**
   * max width of the text rows before they are truncated
   * with an ellipse
   */
  truncateTextWidth?: string,
}

const SnippetContents = ({ truncateTextWidth, photoUrl, displayName, type, caption }) => {
  return (
    <Flex align='center' >
      <Avatar
        size='lg'
        src={photoUrl}
        alt={displayName}
        mr={4}
        name={displayName}
      />
      <Box>
        <H5
          as='h3'
          maxWidth={truncateTextWidth}
          isTruncated
        >
          {displayName}
        </H5>
        <Caption
          isTruncated
          maxWidth={truncateTextWidth}
          mb={1}
        >
          {caption}
        </Caption>
        {type && (
          <Text
            as='h4'
            textStyle='domain'
            color={domainColor(type)}
          >
            {capitalize(type)}
          </Text>
        )}
      </Box>
    </Flex>
  )
}

const useIsPartnerOrOwner = () => {
  const { user } = useUserContext()
  // if (!parentId) return false

  return Boolean(user?.claims['partner'])
}

const Snippet: FC<SnippetProps> = ({
  hit,
  register = (e) => { },
  truncateTextWidth = '200px',
  ...rest }) => {

  const {
    type,
    claims,
    parent,
    photoUrl,
    displayName,
    objectID: id,
    formattedAddress,
  } = hit

  const isPartner = useIsPartnerOrOwner()
  const [isLocked, setIsLocked] = useState(false)
  const overlayBg = useColorModeValue('rgba(255,255,255,0.88)', 'rgba(20,20,20,0.8)')

  const caption = parent?.displayName || formattedAddress

  useEffect(() => {
    const is = !isPartner &&
      type === DomainObjects.project &&
      claims?.some(c => c === 'partner')
    setIsLocked(is)
  }, [isPartner])

  return (
    <ConditionallyWrap
      condition={Boolean(type && id && !isLocked)}
      Wrapper={Link}
      href={(routes[type]?.(id) || null)}
      passHref>
      <Box
        layerStyle='snippet'
        position='relative'
        ref={register}
        cursor={isLocked ? 'not-allowed' : 'pointer'}
        {...(isLocked ? { _hover: {} } : {})}
        {...rest}
      >
        <SnippetContents
          displayName={displayName}
          photoUrl={photoUrl}
          caption={caption}
          truncateTextWidth={truncateTextWidth}
          type={type}
        />
        {isLocked && (
          <Flex
            w='100%'
            h='100%'
            top={0}
            left={0}
            align='center'
            justify='center'
            borderRadius='lg'
            position='absolute'
            bg={overlayBg}
          >
            <Stack>
              <Text
                color='successAlt.default'
                size='xs'
                fontWeight={600}
              >
                Partner content
              </Text>
              <Flex justify='center'>
                <Link href={routes.payment} passHref>
                  <Button
                    size='xs'
                    variant='marketing'
                    leftIcon={<BiLockOpenAlt />}
                  >
                    Unlock
                </Button>
                </Link>
              </Flex>
            </Stack>
          </Flex>
        )}
      </Box>
    </ConditionallyWrap>
  )
}

export { SnippetContents }

export default Snippet
